<template>
    <Dialog :visible="showDialog" class="add-popup" :modal="true" :draggable="false">
        <template #container>
            <div class="dialog-header">
                Add a Serial Number
            </div>
            <div class="dialog-content">
                <span class="text text-center">Add a valid serial number to become its owner and manage its users.</span>
                <div class="flex flex-column w-full justify-content-left mt-2 mb-5">
                    <p class="input-text-label">Serial Number</p>
                    <div class="flex flex-column w-full">
                        <InputText class="text-serial-number" ref="refInputTextSerial" required v-model="newSerialNumber" :class="{ 'p-invalid': (invalidSerialNumberMessage || errorSerialNumberMessage) && isValidSerialNumber(newSerialNumber)!=='success' }"/>
                    </div>
                    <span v-if="serialNumberNotFreeInfo" class="subtext mt-1">
                        <span style="color: var(--inputcomponent-invalid-color);">This serial number is already owned by another user: <span style="font-weight: var(--font-weight-bold);">{{ serialNumberNotFreeInfo.current_owner.owner_first_name ?? "" }}{{ serialNumberNotFreeInfo.current_owner.owner_last_name ? ` ${serialNumberNotFreeInfo.current_owner.owner_last_name}` : "" }}{{ serialNumberNotFreeInfo.current_owner.owner_email ? ` (${serialNumberNotFreeInfo.current_owner.owner_email})` : "" }}.</span></span>
                        <div v-if="showRequestAccess" class="mt-3 align-self-center flex flex-column align-items-center justify-content-center text-center px-6 gap-2">
                            <span v-if="alreadyHaveAccessMessage" class="subtext" style="font-style: italic; color: var(--mysuccess-color);">{{ alreadyHaveAccessMessage }}</span>
                            <span v-if="successfulRequestAccessEmail" class="subtext" style="font-style: italic; color: var(--mysuccess-color);">An email to request access to the serial number has been sent to <span style="font-weight: var(--font-weight-normal);">{{ successfulRequestAccessEmail }}</span>.</span>
                            <span v-if="errorRequestAccessMessage" class="subtext" style="font-style: italic; color: var(--inputcomponent-invalid-color);">{{ errorRequestAccessMessage }}</span>
                            <div v-if="!httpRequestAccessLoading" class="flex flex-column align-items-center justify-content-center">
                                <span id="request-access-text" :class="{ 'cursor-pointer': newSerialNumber, 'disabled': !newSerialNumber || !isHumanVerified }" style="font-size: var(--register-subtext-font-size); font-weight: var(--register-subtext-font-weight);" @click="if (newSerialNumber && isHumanVerified) { requestAccess(); }" v-tooltip.bottom="{ value: newSerialNumber ? '' : 'Please enter the serial number.', showDelay: 800 }">Request access to {{ serialNumberNotFreeInfo.current_owner && serialNumberNotFreeInfo.current_owner.owner_email ? serialNumberNotFreeInfo.current_owner.owner_email : "the owner" }} ?</span> 
                            </div>
                            <div v-else>
                                <span class="text" style="opacity: 0.65;">Wait ...</span>
                            </div>
                            
                        </div>
                    </span>
                    <span v-else-if="invalidSerialNumberMessage || errorSerialNumberMessage" class="subtext mt-1" style="color: var(--inputcomponent-invalid-color);">
                        <span class="align-items-start gap-1">
                            <span>Invalid Serial Number. Check for
                                <a id="clickable-text" class="cursor-pointer" :href="serialNumberZendeskLink" target="_blank" @click="clickedZendeskLink">
                                    <span>potential issues</span>
                                </a>.
                            </span>
                        </span>
                    </span>
                </div>
                <TurnstileWidget widgetId="addSerialTurnstileWidget" @human-verified="(token) => {turnstileToken = token; isHumanVerified = true}"/>
            </div>
            <div class="dialog-footer">
                <div class="buttons">
                    <ButtonModule type="secondary" severity="normal" label="Cancel" @click="emit('close')"/>
                    <ButtonModule type="primary" severity="normal" :label="!httpAddSerialNumberLoading? 'Add' : 'Wait ...'" @click="clickAdd" :disabled="!isHumanVerified || !newSerialNumber || httpAddSerialNumberLoading"/>
                </div>
            </div>
        </template>
    </Dialog>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, onMounted, onBeforeUnmount, watchEffect, computed } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
// vue
import ButtonModule from '@/components/Modules/ButtonModule'
import TurnstileWidget from '@/components/Modules/TurnstileWidget.vue';
// Others
import InputsUtils from '@/composables/InputsUtils'
import toastService from '@/composables/toastService';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router'
import UserUtils from '@/composables/UserUtils';

import eventBus from '@/composables/eventBus.js';

const store = useStore()
const router = useRouter()
const { isValidSerialNumber } = InputsUtils()
const { logoutUser } = UserUtils(store, router)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchAddSerialNumber, fetchRequestAccess } = useApiRequest()
const httpAddSerialNumberLoading = ref(false)
const httpAddSerialNumberData = ref(null)
const httpRequestAccessLoading = ref(false)
const httpRequestAccessData = ref(null)

// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
const applicationCode = computed(() => store.state.applicationWebView.applicationCode)

// --- EMITS ---
const emit = defineEmits(['close'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
})
const { showDialog } = toRefs(props)

// --- VARIABLES ---
const newSerialNumber = ref(null)
const invalidSerialNumberMessage = ref(null)
const errorSerialNumberMessage = ref(false)
const serialNumberNotFreeInfo = ref(null)
const showRequestAccess = ref(false)

// --- Validate add ---
const clickAdd = async () => {
    invalidSerialNumberMessage.value = null
    errorSerialNumberMessage.value = false
    serialNumberNotFreeInfo.value = null
    showRequestAccess.value = false
    alreadyHaveAccessMessage.value = null
    successfulRequestAccessEmail.value = null
    errorRequestAccessMessage.value = null
    // If nothing, return
    if (!newSerialNumber.value) { return }
    // Check if not already in my licenses list
    if (!isWebView.value && store.state.licenses.licenses.some((license) => license.admin.id == store.state.user.user.id && license.serial_number == newSerialNumber.value)) {
        toastService.add({ severity: 'warn', summary: "Add a Serial Number: Warning", detail: 'You already own this license.', life: 5000 });
        return
    }
    else if (isWebView.value && store.state.applicationWebView.licenses.some((license) => license.serial_number == newSerialNumber.value)) {
        toastService.add({ severity: 'warn', summary: "Add a Serial Number: Warning", detail: 'This license is already linked to you.', life: 5000 });
        return
    }
    // Check if serial number is valid
    const messageValidity = isValidSerialNumber(newSerialNumber.value)
    if (messageValidity !== 'success') {
        invalidSerialNumberMessage.value = messageValidity
        return
    }
    
    // HTTP request
    httpAddSerialNumberLoading.value = true
    try {
        const requestInfo = isWebView.value ? { serial_number: newSerialNumber.value, cf_turnstile_response: turnstileToken.value, application_code: applicationCode.value } : { serial_number: newSerialNumber.value, cf_turnstile_response: turnstileToken.value }
        httpAddSerialNumberData.value = await fetchAddSerialNumber(requestInfo)
        if (!isWebView.value) {
            // Add the new serial number info in the cache
            store.commit('licenses/addLicense', httpAddSerialNumberData.value.license)
            // Toast + close the window
            //toastService.add({ severity: 'success', summary: "Add a Serial Number: Success", detail: 'You are the owner of a new license !', life: 5000 });
            eventBus.dispatchEvent(new CustomEvent('new-serial-number-added', { detail: { applicationName: httpAddSerialNumberData.value.license.product.application } }));
        }
        else { // webview
            // Add the new serial number info in the cache
            store.commit('applicationWebView/addLicense', httpAddSerialNumberData.value.license)
        }
        
        emit('close')
    } catch (err) {
        turnstileToken.value = null;
        isHumanVerified.value = false;
        window.turnstile.reset("#addSerialTurnstileWidget")
        handleAddSerialNumberError(err)
    } finally {
        httpAddSerialNumberLoading.value = false
    }
}
// Handle an error from the server
const handleAddSerialNumberError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Add a Serial Number: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Add a Serial Number: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Add a Serial Number: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Add a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') {
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else {  toastService.add({ severity: 'error', summary: `Add a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND / SERIAL_INVALID (404)
                if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID => invalidSerialNumberMessage
                    errorSerialNumberMessage.value = true
                }
                else if (errorCode == 'ACCOUNT_NOT_FOUND') { // ACCOUNT_NOT_FOUND => toast + logout
                    toastService.add({ severity: 'error', summary: "Add a Serial Number: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else { toastService.add({ severity: 'error', summary: `Add a Serial Number: CAPTCHA Error.`, detail: "The CAPTCHA you entered is invalid or has expired. Please try again.", life: 5000 }); } // It only happens when the CAPTCHA is not correct
                break;
            case 409: // SERIAL_NOT_FREE (409) => toast
                if (errorCode == 'SERIAL_NOT_FREE') {
                    serialNumberNotFreeInfo.value = {
                        error_message: errorMessage,
                        current_owner: error.response.data.additional_info
                    }
                    if (serialNumberNotFreeInfo.value.current_owner.owner_id) {
                        showRequestAccess.value = true
                    }
                }
                else {  toastService.add({ severity: 'error', summary: `Add a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Add a Serial Number: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Add a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Add a Serial Number: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Add a Serial Number: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// -- Request access (in case the serial number to be added is already owner by another user) --
const alreadyHaveAccessMessage = ref(null)
const successfulRequestAccessEmail = ref(null)
const errorRequestAccessMessage = ref(null)
const requestAccess = async () => {
    errorSerialNumberMessage.value = null
    alreadyHaveAccessMessage.value = null
    successfulRequestAccessEmail.value = null
    errorRequestAccessMessage.value = null
    // If nothing, return
    if (!newSerialNumber.value) { return }
    // Check if email is valid
    const messageValidity = isValidSerialNumber(newSerialNumber.value)
    if (messageValidity !== 'success') {
        errorSerialNumberMessage.value = messageValidity
        return
    }

    // HTTP request
    httpRequestAccessLoading.value = true
    try {
        httpRequestAccessData.value = await fetchRequestAccess({ serial_number: newSerialNumber.value, owner_id: serialNumberNotFreeInfo.value.current_owner.owner_id, owner_email: serialNumberNotFreeInfo.value.current_owner.owner_email, cf_turnstile_response: turnstileToken.value })
        successfulRequestAccessEmail.value = serialNumberNotFreeInfo.value.current_owner.owner_email // here, should be replaced by the actual email address of the owner
    } catch (err) {
        handleRequestAccessError(err)
    } finally {
        turnstileToken.value = null;
        isHumanVerified.value = false;
        window.turnstile.reset("#addSerialTurnstileWidget")
        httpRequestAccessLoading.value = false
    }
}
// Handle an error from the server
const handleRequestAccessError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Request Access: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM / INPUTS_INVALID (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Request Access: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Request Access: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else if (errorCode == 'INPUTS_INVALID') { // INPUTS_INVALID
                    errorSerialNumberMessage.value = "Please enter a valid serial number."
                }
                else { toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / EMAIL_SENDING_LIMIT_REACHED (401)
                if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                } else if (errorCode == 'EMAIL_SENDING_LIMIT_REACHED') { // EMAIL_SENDING_LIMIT_REACHED
                    errorRequestAccessMessage.value = "The access request email has been sent very recently. Please wait a few moment before trying to send the request again."
                }
                else { toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // ACCOUNT_NOT_FOUND / SERIAL_INVALID (404)
                if (errorCode == 'SERIAL_INVALID') { // SERIAL_INVALID => invalidSerialNumberMessage
                    errorSerialNumberMessage.value = true
                } else if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Request Access: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else { toastService.add({ severity: 'error', summary: `Request Access: CAPTCHA Error.`, detail: "The CAPTCHA you entered is invalid or has expired. Please try again.", life: 5000 }); } // It only happens when the CAPTCHA is not correct
                break;
            case 409: // ALREADY_AUTHORIZED_USER / SERIAL_FREE / DIFFERENT_OWNER / DIFFERENT_OWNER_EMAIL (409)
                if (errorCode == 'ALREADY_AUTHORIZED_USER') { // ALREADY_AUTHORIZED_USER
                    alreadyHaveAccessMessage.value = "It seems that you already have access to this serial number. Please try refreshing the dashboard to see your license."
                } else if (errorCode == 'SERIAL_FREE') { // SERIAL_FREE
                    invalidSerialNumberMessage.value = null
                    errorSerialNumberMessage.value = false
                    serialNumberNotFreeInfo.value = null
                    showRequestAccess.value = false
                    toastService.add({ severity: 'warn', summary: `Request Access: No owner assigned.`, detail: 'The serial number seems to be free to attach, meaning there is no more assigned owner. You can try again to add it to become its owner.', life: 10000 });
                } else if (errorCode == 'DIFFERENT_OWNER') { // DIFFERENT_OWNER
                    if (error.response && error.response.data && error.response.data.additional_info) {
                        serialNumberNotFreeInfo.value.current_owner = error.response.data.additional_info
                        errorRequestAccessMessage.value = 'It seems that the owner of the license has changed. If you want to send an access request to this new owner, please try again.'
                    } else { toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                } else if (errorCode == 'DIFFERENT_OWNER_EMAIL') { // DIFFERENT_OWNER_EMAIL
                    if (error.response && error.response.data && error.response.data.additional_info) {
                        serialNumberNotFreeInfo.value.current_owner = error.response.data.additional_info
                        errorRequestAccessMessage.value = 'It seems that the owner changed its account email address. If you want to send an access request to this new email address, please try again.'
                    } else { toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                }
                else { toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }                
                break;
            case 500: // DATABASE_ERROR / INTERNAL_ERROR / EMAIL_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR' || errorCode == 'INTERNAL_ERROR') {
                    toastService.add({ severity: 'error', summary: "Request Access: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                } else if (errorCode == 'EMAIL_ERROR') { // EMAIL_ERROR
                    toastService.add({ severity: 'error', summary: "Request Access: Email Sending.", detail: 'Our server did not manage sending the access request email to the address you provided. Make sure it is a valid address email.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Request Access: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Request Access: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}


// --- Support link ---
const serialNumberZendeskLink = "https://softron.zendesk.com/hc/en-us/articles/14666915884572"
const clickedZendeskLink = () => {
    // (in case it is in the web view in the app, ask the app to open it also, because the href thing will not work if it is in the web view)
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(serialNumberZendeskLink);
    }
}

// --- TURNSTILE ---
const turnstileToken = ref(null)
const isHumanVerified = ref(false) // turnstile

/// --- Reset when closing ---
watchEffect(() => {
    if (!showDialog.value) { newSerialNumber.value = null; invalidSerialNumberMessage.value = null; errorSerialNumberMessage.value = false; serialNumberNotFreeInfo.value = null; showRequestAccess.value = false; alreadyHaveAccessMessage.value = null; successfulRequestAccessEmail.value = null; errorRequestAccessMessage.value = null; isHumanVerified.value = false; turnstileToken.value = null; }
})

// --- Focus + ENTER key listener ---
const refInputTextSerial = ref(null)
watchEffect(() => {
    if (refInputTextSerial.value) { refInputTextSerial.value.$el.focus() }
})
onMounted(() => {
    window.addEventListener('keydown', handleKeyPressed)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyPressed)
}) 
const handleKeyPressed = (event) => {
    if (event.keyCode === 13) { // ENTER
        if (newSerialNumber.value) { clickAdd() }
    }
}
</script>

<style scoped>
/* Request access */
#request-access-text {
    text-decoration: none;
    color: var(--button-normal-color);
    border-bottom: 1px solid transparent;
}
#request-access-text.disabled {
    opacity: 0.6;
}
#request-access-text:hover:not(.disabled) {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>